import { type ClassValue, clsx } from "clsx"
import { Role, Shift, User, TicketType } from "@shiftly/shared/src/index";
import { Permissions } from '@shiftly/shared/src/permissions';
import { twMerge } from "tailwind-merge"
import { OT_SHIFT_CODES_KEYS } from "@shiftly/shared/src/shiftCodes";
import { DateTime } from "luxon";

/**
 * Combines multiple class names into a single string, merging Tailwind CSS classes properly.
 * This is a utility function that uses clsx and tailwind-merge to handle class name conflicts.
 * 
 * @param inputs - Class names to combine
 * @returns A merged class name string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Gets the initials from a user's full name
 * 
 * @param full_name - The user's full name
 * @returns The user's initials (1-2 characters)
 */
export function getUserInitials(full_name: string): string {
  if (!full_name) return 'UN';
  
  const nameParts = full_name.split(' ');
  if (nameParts.length === 1) return nameParts[0].substring(0, 2).toUpperCase();
  return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
}

/**
 * Gets a user's full name and initials from a list of users by user ID
 * 
 * @param user_id - The user's ID
 * @param users - Array of user objects
 * @returns Object containing the user's full name and initials
 */
export function getUserInfo(user_id: string, users: { id: string; full_name: string }[]): { full_name: string; initials: string } {
  const user = users.find(u => u.id === user_id);
  const full_name = user ? user.full_name : 'Unknown User';
  return {
    full_name,
    initials: getUserInitials(full_name)
  };
}

export function getUserHighestRole(user: User, roles: Role[]): Role | undefined {
  const userRoles = user.roles || [];
  const highestRoleId = userRoles.sort((a, b) => {
    const roleA = roles.find(r => r.id === a);
    const roleB = roles.find(r => r.id === b);
    return (roleB?.level || 0) - (roleA?.level || 0);
  })[0];
  return roles.find(r => r.id === highestRoleId);
}

export const getMonthBoundary = (date: DateTime) => {
  const utcDate = date.zoneName === 'UTC' ? date : date.setZone('UTC');

  const start = utcDate.startOf("month").set({ day: 1, hour: 6, minute: 0, second: 0, millisecond: 0 });
  const end = start.plus({ month: 1 }).set({ day: 1, hour: 5, minute: 59, second: 59, millisecond: 999 });
  return { start, end };
};

export const getMonthBoundaryBST = (date: DateTime) => {
  const start = date.setZone('Europe/London').startOf('month').set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
  const end = date.setZone('Europe/London').plus({ months: 1 }).startOf('month').set({ hour: 5, minute: 59, second: 59, millisecond: 999 });
  return { start, end };
};

export const getMonthBoundaryForSchedule = (date: DateTime) => {
  const start = date.set({ day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 });
  const end = start.plus({ months: 1 });
  return { start, end };
};

export const getRollingWindowBoundary = (days: number) => {
  const now = DateTime.now().setZone('Europe/London');
  const endDate = now.set({ hour: 5, minute: 59, second: 59, millisecond: 999 });
  const startDate = endDate.minus({ days }).plus({ minutes: 1 }).set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
  
  return {
    startDate: startDate,
    endDate: endDate
  };
};

export function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export const getShiftColor = (shift: Shift, permissions: string[], user: User | undefined, roles: Role[] | undefined) => {
  // If verification required
  if (shift.verification_required && permissions?.includes(Permissions.SHIFTS_VERIFY)) return 'red';
  // If livestream shift
  if (shift.is_livestream_shift && shift.livestream_earned <= 0 && permissions?.includes(Permissions.SHIFTS_EDIT)) return 'red';
  // If requires OT worker
  if (!shift.submitted && OT_SHIFT_CODES_KEYS.includes(shift.shift_code) && shift.shift_code != 'TEMP OT WKND FP' && shift.user_id == undefined && permissions?.includes(Permissions.SHIFTS_EDIT)) return 'pink';
  // If shift not submitted
  if (!shift.submitted && shift.clocked_in) return 'orange';
  // If temp OT WKND FP shift
  if (shift.shift_code == 'TEMP OT WKND FP') return 'blue';
  // If LAUNCH shift
  if (shift.shift_code == 'LAUNCH') return 'teal';
  // If OT shift
  if (OT_SHIFT_CODES_KEYS.includes(shift.shift_code)) return 'yellow';
  // If unassigned and not OT
  if (!shift.submitted && !shift.user_id && !OT_SHIFT_CODES_KEYS.includes(shift.shift_code)) return 'purple';
  // If trial worker - check if any of the roles is a trial role
  if (user && roles && roles.length > 0 && roles.find(role => role?.name.toLowerCase() === 'trial')) return 'purple';
  // If unworked shift
  if (!shift.clocked_in) return 'gray';
  // If shift is worked
  return 'green';
}

export const getShiftColorCalendar = (shift: Shift, permissions: string[], user: User | undefined, roles: Role[] | undefined) => {
  const color = getShiftColor(shift, permissions, user, roles);
  return `${color}-shift`;
}

export const getShiftColorTailwind = (shift: Shift, permissions: string[], user: User | undefined, roles: Role[] | undefined) => {
  const color = getShiftColor(shift, permissions, user, roles);
  if (color === 'gray') return 'bg-zinc-100/50 border-zinc-300 dark:bg-zinc-700/30 dark:border-zinc-600';
  return `bg-${color}-100/30 border-${color}-500 dark:bg-${color}-600/20 dark:border-${color}-500`;
}

/**
 * Gets the human-readable name for a ticket type
 * 
 * @param type - The ticket type
 * @returns The human-readable name for the ticket type
 */
export function getTicketTypeName(type: TicketType): string {
  switch (type) {
    case 'urgent': return 'Urgent';
    case 'misc': return 'Misc';
    case 'payment': return 'Payment/Invoicing';
    case 'staffing': return 'Account/Shift Change';
    case 'complaints': return 'Complaints';
    case 'timeOff': return 'Time Off';
    case 'timeOffUrgent': return 'Time Off (Urgent)';
    case 'streamTimes': return 'Stream Times';
    case 'accountAccess': return 'Account Access';
    case 'overwatch': return 'Overwatch';
    case 'scheduling': return 'Scheduling';
    default: return 'Unknown';
  }
}